/* eslint-disable no-underscore-dangle */
import { Component, Input } from '@angular/core';

import { HisUiService, GenericValue, DynamicComponent, HisBaseGlobalsService } from 'hisng';

import { DataDictionaryUtils } from 'app/fs/res/common/utils/data-dictionary-utils';
import { RightsUtils } from 'app/fs/res/common/utils/rights-utils';
import { Publication } from '../../../model/publication';
import { PublicationCreator } from '../../../model/publication-creator';
import { PublicationCreatorOrgunit } from '../../../model/publication-creator-orgunit';
import { GlobalIdentifierEnum, GlobalIdentifierType } from '../../../model/enums/global-identifier-enum';
import { GlobalIdentifier } from '../../../model/global-identifier';
import { CreatorType } from '../../../model/enums/creator-type-enum';

@Component({
    selector: 'res-publication-details-panel',
    templateUrl: './publication-details-panel.component.html',
})
export class PublicationDetailsPanelComponent {
    @Input() detailsForChangeRequest = true;
    @Input('publication') set publicationData(publication: Publication) {
        this.publication = publication;
        if (this.publication.changeRequest) {
            this.setDynAttributesList(this.publication.changeRequest._dynamicComponent);
        }
    }
    public publication: Publication;
    public dynAttributesList: {labelKey: string; value: string}[] = [];
    public identifierEnum: typeof GlobalIdentifierEnum = GlobalIdentifierEnum; // used in html
    public hasEditRights = false;
    public showParentLink = true;
    public showCitationDialog = false;
    public dateFormat: string;
    public personRights: Map<number, boolean> = new Map<number, boolean>();

    constructor(
        public rights: RightsUtils,
        public hisUiService: HisUiService,
        private hisBaseGlobalsService: HisBaseGlobalsService ) {
    }

    public getDdKey(source: string) {
        if ( this.publication && this.publication.publicationType ) {
            return DataDictionaryUtils.dDKeyPublicationType(this.publication.publicationType, source);
        }
        return undefined;
    }

    public isEmpty( input: any ): boolean {
        return !input || !input.length;
    }

    public isAnalogIdentifier(identifier: GlobalIdentifier): boolean {
        return identifier && GlobalIdentifierType.analogIdentifiers().findIndex(c =>
            c === identifier.globalIdentifierType.hiskeyId) > -1;
    }

    public getSubjectAreaString(subjectArea: GenericValue) {
        return '[\"' + subjectArea.id + '\"]';
    }

    public getResearchAreaString(researchArea: GenericValue) {
        return '[\"' + researchArea.id + '\"]';
    }


    public isTypeOfEditor(): boolean {
        return this.publication.publicationCreatorType && CreatorType.editors().findIndex( c =>
            c === this.publication.publicationCreatorType.hiskeyId ) > -1;
    }

    public isTypeOfAuthor(): boolean {
        return this.publication.publicationCreatorType && CreatorType.authors().findIndex( c =>
            c === this.publication.publicationCreatorType.hiskeyId ) > -1;
    }

    public getTitleForCreatorLink(): string {
        if ( this.isTypeOfAuthor() ) {
            return 'fs.res.publication.author';
        } else if ( this.isTypeOfEditor() ) {
            return 'fs.res.publication.editor';
        }
        return undefined;
    }

    public getCreatorsAsString() {
        let text = '';
        if (this.publication.creators) {
            for (let i = 0; i < this.publication.creators.length; i++) {
                const creator = this.publication.creators[i];
                if (this.showAsPerson(creator)) {
                    text += this.getCreatorFullName(creator);
                } else if (this.showAsOrganization(creator)) {
                    text += creator.creatorOrganizations[0].organization.text === creator.creatorname ?
                        creator.creatorOrganizations[0].organization.text :
                        '&ngsp;(' + this.getCreatorOrganizations(creator.creatorOrganizations) + ')';
                } else if (this.showAsExternalOrganization(creator)) {
                    text += creator.creatorOrganizations[0].researchPartner.defaulttext === creator.creatorname ?
                        creator.creatorOrganizations[0].researchPartner.defaulttext :
                        '&ngsp;(' + this.getCreatorOrganizations(creator.creatorOrganizations) + ')';
                } else {
                    text += this.getCreatorFullName(creator);
                }
                text += i < this.publication.creators.length - 1 ? ', ' : '';
            }
        }
        return text;
    }

    private getCreatorFullName( creator: PublicationCreator ): string  {
        const prefix = creator.nameprefix !== null ? creator.nameprefix : '';
        const suffix = creator.namesuffix !== null ? creator.namesuffix : '';
        return creator.firstname.concat(' ' + prefix, ' ' + creator.creatorname, ' ' + suffix ).trim();
    }

    public getCreatorOrganizations( organizations: PublicationCreatorOrgunit[] ): string {
        return organizations.map(o => o.organization ? o.organization?.text : o.researchPartner.defaulttext ).join( ', ' );
    }

    private showAsPerson( creator: PublicationCreator ): boolean {
        return !this.isEmpty( creator.firstname );
    }

    private showAsOrganization( creator: PublicationCreator ): boolean {
        return !!creator.creatorOrganizations[0]?.organization;
    }

    private showAsExternalOrganization( creator: PublicationCreator ): boolean {
        return !!creator.creatorOrganizations[0]?.researchPartner;
    }

    private setDynAttributesList(dynComponent: DynamicComponent) {
        this.dynAttributesList = [];
        if (dynComponent) {
            let dynObjects = dynComponent.dynamicObjects;
            dynComponent.guiComponent.children.forEach(guiTab => {
                for(let guiFieldset of guiTab.children) {

                    for(let field of guiFieldset.fields){
                        let dynObj = dynObjects.find(c =>
                            c.objGuid === field.objectUID && c.dynamicClassName !== 'fs.res.publication.Publication'
                        );
                        if (dynObj) {
                            const val = dynObj.values.find(v => v.attributeName === field.attributeName);
                            if (val && !this.isHidden(field.ddKey)) {
                                this.dynAttributesList.push({labelKey: field.label, value: val.oldValue});
                            }
                        }
                    }
                }
            });
        }
    }

    public isHidden( ddKey: string ) {
        return this.hisBaseGlobalsService.getDd(ddKey)?.hidden;
    }
}
